import { useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useDecideLeave,
  useLogTeamLeave,
  useManagerLeaveMonthlySummary,
  useManagerLeaveSummary,
  useTeamLeave,
} from "@/api/queries";
import type { LeaveRequest, LeaveSummaryMember, LeaveType } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { StackedBarChart, StatTile, TrendChart } from "@/components/charts";
import { LeaveBalanceMeter } from "@/components/leave-balance-meter";
import { STATUS_SERIES_COLOR } from "@/components/charts/stacked-bar-chart";
import { Avatar } from "@/components/ui/avatar";
import { Badge, FiledLateBadge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, ErrorState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { approvedDaysInYear, leaveByType, leaveDaysByMonth } from "@/lib/analytics";
import { currentMonth, shiftMonth, todayStr } from "@/lib/calendar";
import { orgYear } from "@/lib/org-time";
import { formatDate, formatDays, formatTimestampDate } from "@/lib/format";
import { EMPLOYMENT_TYPE_LABEL, LEAVE_TYPE_SHORT, leaveLabel, UNRECORDED_ABSENCE_LABEL } from "@/lib/labels";
import { LeaveDetailDialog, ReasonCell } from "@/pages/shared/leave-detail-dialog";
import { WfhApprovalsPanel } from "@/pages/shared/wfh-approvals";

const MONTHLY_SUMMARY_TYPE_ORDER: LeaveType[] = Object.keys(LEAVE_TYPE_SHORT) as LeaveType[];

export function ManagerLeavePage() {
  const leave = useTeamLeave();
  const summary = useManagerLeaveSummary();
  const decide = useDecideLeave();
  const [viewing, setViewing] = useState<LeaveRequest | null>(null);
  const [confirming, setConfirming] = useState<{
    request: LeaveRequest;
    status: "approved" | "rejected";
  } | null>(null);
  const [logging, setLogging] = useState(false);
  // Sibling tabs, not one merged queue. Approving a day off and approving a day
  // worked from a different desk are different decisions with different
  // consequences, and a single list would flatten that into a type column.
  const [tab, setTab] = useState<"leave" | "wfh">("leave");
  const [summaryMonth, setSummaryMonth] = useState(currentMonth());
  const monthlySummary = useManagerLeaveMonthlySummary(summaryMonth);
  const monthlyMembers = monthlySummary.data?.members ?? [];
  const presentTypes = MONTHLY_SUMMARY_TYPE_ORDER.filter((type) =>
    monthlyMembers.some((m) => m.byType[type] !== undefined),
  );

  const requests = leave.data ?? [];
  const pending = requests.filter((r) => r.status === "pending");
  const byType = useMemo(() => leaveByType(requests), [requests]);
  const byMonth = useMemo(() => leaveDaysByMonth(requests), [requests]);

  // This year only — see approvedDaysInYear.
  const approvedDays = approvedDaysInYear(requests);

  async function onDecide() {
    if (!confirming) return;
    try {
      await decide.mutateAsync({ id: confirming.request.id, status: confirming.status });
      toast.success(
        `Leave ${confirming.status} for ${confirming.request.employee_name ?? "this person"}.`,
      );
      setConfirming(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not record that decision.");
    }
  }

  if (leave.error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Scoped to your direct reports" title="Team leave" />
        <Panel>
          <ErrorState message={leave.error.message} onRetry={() => void leave.refetch()} />
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Scoped to your direct reports"
        title="Team leave"
        sub={
          tab === "leave"
            ? "Approve or decline requests from your direct reports. Every decision here shows up immediately on the HR and Admin view."
            : "Approve or decline work-from-home requests, and see how much of it your team is taking week by week."
        }
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as "leave" | "wfh")}>
        <TabsList>
          <TabsTrigger value="leave">Leave</TabsTrigger>
          <TabsTrigger value="wfh">Work from home</TabsTrigger>
        </TabsList>

        <TabsContent value="leave" className="mt-6 flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Waiting on you"
          value={pending.length}
          icon="warning-circle"
          loading={leave.isLoading}
          meta={pending.length === 0 ? "Nothing outstanding" : "Needs a decision"}
        />
        <StatTile label="Requests on record" value={requests.length} icon="leave" loading={leave.isLoading} />
        <StatTile
          label="Approved days"
          value={approvedDays}
          icon="calendar-check"
          loading={leave.isLoading}
          meta="Across your team, this year"
        />
        <StatTile
          label="Team comp off"
          value={formatDays(
            (summary.data?.members ?? []).reduce((sum, m) => sum + m.compOff.available, 0),
          )}
          icon="check-circle"
          loading={summary.isLoading}
          meta="Earned and not yet taken"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <TrendChart
          title="Approved leave days by month"
          note={`Calendar year ${summary.data?.year ?? orgYear()}, your team only`}
          data={byMonth}
          xKey="month"
          series={[{ key: "days", label: "Days approved" }]}
          area
          loading={leave.isLoading}
          emptyBody="Nothing has been approved for your team this year."
        />

        <StackedBarChart
          title="Requests by leave type"
          note="How your team's requests were decided"
          data={byType as unknown as Record<string, string | number>[]}
          categoryKey="type"
          orientation="horizontal"
          series={[
            { key: "approved", label: "Approved", color: STATUS_SERIES_COLOR.approved },
            { key: "pending", label: "Pending", color: STATUS_SERIES_COLOR.pending },
            { key: "rejected", label: "Rejected", color: STATUS_SERIES_COLOR.rejected },
            { key: "withdrawn", label: "Withdrawn", color: STATUS_SERIES_COLOR.withdrawn },
          ]}
          loading={leave.isLoading}
          height={Math.max(180, byType.length * 44)}
          emptyBody="Your team has not submitted any leave requests yet."
        />
      </div>

      <Panel>
        <PanelHeader
          title="Requests"
          note="Only requests from employees on your own team reach this screen."
          actions={
            <Button variant="secondary" size="sm" iconLeft="plus" onClick={() => setLogging(true)}>
              Log leave
            </Button>
          }
        />
        <Tabs defaultValue="pending">
          <div className="border-b border-line px-4 py-2.5">
            <TabsList>
              <TabsTrigger value="pending">Pending ({pending.length})</TabsTrigger>
              <TabsTrigger value="all">All ({requests.length})</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="pending">
            <RequestTable
              requests={pending}
              loading={leave.isLoading}
              emptyTitle="Nothing waiting on you"
              emptyBody="Every request from your team has been actioned."
              onDecide={(request, status) => setConfirming({ request, status })}
              onView={setViewing}
            />
          </TabsContent>
          <TabsContent value="all">
            <RequestTable
              requests={requests}
              loading={leave.isLoading}
              emptyTitle="No requests yet"
              emptyBody="Requests appear here as soon as someone on your team applies."
              onDecide={(request, status) => setConfirming({ request, status })}
              onView={setViewing}
            />
          </TabsContent>
        </Tabs>
      </Panel>

      <Panel>
        <PanelHeader
          title="Entitlement balances"
          note={`Per direct report, for calendar year ${summary.data?.year ?? orgYear()}`}
        />
        {summary.isLoading ? (
          <SkeletonTable rows={4} cols={4} />
        ) : (summary.data?.members ?? []).length === 0 ? (
          <EmptyState icon="team" title="No direct reports yet" />
        ) : (
          <ul className="divide-y divide-line">
            {(summary.data?.members ?? []).map((member) => (
              <li key={member.id} className="px-4 py-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Avatar name={member.name} size="sm" />
                  <span className="text-sm font-medium text-ink">{member.name ?? "Unnamed"}</span>
                  <Badge tone="outline">{EMPLOYMENT_TYPE_LABEL[member.employment_type]}</Badge>
                  <span className="ml-auto text-xs text-muted">
                    Comp off available {formatDays(member.compOff.available)}
                  </span>
                </div>
                <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(member.balance).map(([type, line]) =>
                    line ? (
                      <LeaveBalanceMeter key={type} type={type} line={line} />
                    ) : null,
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel>
        <PanelHeader
          title="Monthly leave summary"
          note={formatDate(`${summaryMonth}-01`, "MMMM yyyy") ?? summaryMonth}
          actions={
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Previous month"
                onClick={() => setSummaryMonth(shiftMonth(summaryMonth, -1))}
              >
                <Icon name="caret-left" size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Next month"
                disabled={summaryMonth >= currentMonth()}
                onClick={() => setSummaryMonth(shiftMonth(summaryMonth, 1))}
              >
                <Icon name="caret-right" size={16} />
              </Button>
            </div>
          }
        />
        {monthlySummary.isLoading ? (
          <SkeletonTable rows={4} cols={4} />
        ) : monthlyMembers.length === 0 ? (
          <EmptyState icon="team" title="No direct reports yet" />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              {presentTypes.map((type) => (
                <TH key={type} numeric>
                  {LEAVE_TYPE_SHORT[type]}
                </TH>
              ))}
              <TH numeric>{UNRECORDED_ABSENCE_LABEL}</TH>
            </THead>
            <TBody>
              {monthlyMembers.map((member) => (
                <TR key={member.id}>
                  <TDPrimary>
                    <span className="flex items-center gap-2">
                      <Avatar name={member.name} size="sm" />
                      <span className="truncate">{member.name ?? "Unnamed"}</span>
                    </span>
                  </TDPrimary>
                  {presentTypes.map((type) => (
                    <TD key={type} numeric>
                      {formatDays(member.byType[type] ?? 0)}
                    </TD>
                  ))}
                  <TD numeric>{formatDays(member.lwp)}</TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

        </TabsContent>

        <TabsContent value="wfh" className="mt-6">
          <WfhApprovalsPanel scope="manager" />
        </TabsContent>
      </Tabs>

      {viewing && <LeaveDetailDialog request={viewing} onClose={() => setViewing(null)} />}

      {confirming && (
        <Dialog open onOpenChange={(open) => !open && setConfirming(null)}>
          <DialogContent>
            <DialogHeader
              title={confirming.status === "approved" ? "Approve this leave?" : "Decline this leave?"}
              description={`${confirming.request.employee_name ?? "This person"} · ${leaveLabel(
                confirming.request.leave_type,
              )} · ${formatDate(confirming.request.start_date)}${
                confirming.request.start_date !== confirming.request.end_date
                  ? ` to ${formatDate(confirming.request.end_date)}`
                  : ""
              }`}
            />
            <DialogBody>
              <dl className="divide-y divide-line rounded-lg border border-line">
                <div className="flex justify-between gap-4 px-3 py-2">
                  <dt className="text-[13px] text-muted">Days</dt>
                  <dd className="text-[13px] font-medium text-ink">
                    {confirming.request.days}
                  </dd>
                </div>
                <div className="flex justify-between gap-4 px-3 py-2">
                  <dt className="text-[13px] text-muted">Reason</dt>
                  <dd className="max-w-[60%] whitespace-pre-wrap break-words text-right text-[13px] text-ink">
                    {confirming.request.reason ?? "Not given"}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-xs leading-relaxed text-muted">
                This decision is final. Once recorded it cannot be changed from this screen.
              </p>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirming(null)} disabled={decide.isPending}>
                Cancel
              </Button>
              <Button
                variant={confirming.status === "approved" ? "primary" : "danger"}
                onClick={onDecide}
                loading={decide.isPending}
              >
                {confirming.status === "approved" ? "Approve leave" : "Decline leave"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {logging && (
        <LogLeaveDialog members={summary.data?.members ?? []} onClose={() => setLogging(false)} />
      )}
    </div>
  );
}

function LogLeaveDialog({
  members,
  onClose,
}: {
  members: LeaveSummaryMember[];
  onClose: () => void;
}) {
  const log = useLogTeamLeave();
  /*
    Empty until somebody chooses. It used to seed to the first member, which the
    browser then painted as selected while the list was still loading and the
    state was still "" — a name on screen, and "Pick who this leave is for." on
    save. The placeholder option below keeps what is shown and what is held the
    same value at every moment.
  */
  const [userId, setUserId] = useState<string>("");
  const [leaveType, setLeaveType] = useState<LeaveType | "">("");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);


  /*
    Which types this PERSON can be given, straight from the server's own answer:
    summarizeByType builds each member's balance from eligibleLeaveTypesFor, so
    the keys of that balance are the eligibility rule, already applied. Listing
    every type that exists instead meant offering Earned Leave for an intern and
    finding out only when the backend rejected the save.

    Before a person is chosen there is nobody to be eligible, so the list is
    empty and the field waits.
  */
  const selectedMember = members.find((m) => String(m.id) === userId);
  const eligibleTypes = (
    selectedMember ? (Object.keys(selectedMember.balance) as LeaveType[]) : []
  ).sort((a, b) => MONTHLY_SUMMARY_TYPE_ORDER.indexOf(a) - MONTHLY_SUMMARY_TYPE_ORDER.indexOf(b));

  // A type that stops being eligible when the person changes is not a choice.
  const activeType: LeaveType | "" =
    leaveType && eligibleTypes.includes(leaveType) ? leaveType : "";

  async function onSave() {
    setError(null);
    if (!userId) {
      setError("Pick who this leave is for.");
      return;
    }
    if (!leaveType) {
      setError("Pick which type of leave this is.");
      return;
    }
    try {
      await log.mutateAsync({
        userId: Number(userId),
        startDate,
        endDate,
        reason: reason.trim(),
        leaveType,
      });
      toast.success("Leave logged and marked approved.");
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not log that leave.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title="Log leave for a team member"
          description="Recorded as already decided — it lands straight as approved and counts against their balance right away."
        />
        <DialogBody className="flex flex-col gap-4">
          <Field label="Employee" htmlFor="log-leave-user">
            <Select id="log-leave-user" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Select a person…</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name ?? "Unnamed"}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Leave type" htmlFor="log-leave-type">
            <Select
              id="log-leave-type"
              value={activeType}
              onChange={(e) => setLeaveType(e.target.value as LeaveType)}
            >
              <option value="">Select a leave type…</option>
              {eligibleTypes.map((type) => (
                <option key={type} value={type}>
                  {leaveLabel(type)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Start date" htmlFor="log-leave-start">
              <Input
                id="log-leave-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label="End date" htmlFor="log-leave-end">
              <Input
                id="log-leave-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Reason" htmlFor="log-leave-reason" hint="Optional for Optional Holiday.">
            <Textarea id="log-leave-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {error && (
            <p role="alert" className="flex items-start gap-1.5 text-xs text-bad">
              <Icon name="warning-circle" size={16} />
              <span>{error}</span>
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={log.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} loading={log.isPending}>
            Log leave
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestTable({
  requests,
  loading,
  emptyTitle,
  emptyBody,
  onDecide,
  onView,
}: {
  requests: LeaveRequest[];
  loading: boolean;
  emptyTitle: string;
  emptyBody: string;
  onDecide: (request: LeaveRequest, status: "approved" | "rejected") => void;
  onView: (request: LeaveRequest) => void;
}) {
  if (loading) return <SkeletonTable rows={5} cols={6} />;
  if (requests.length === 0)
    return <EmptyState icon="check-circle" title={emptyTitle} body={emptyBody} />;

  return (
    <TableWrap>
      <THead>
        <TH>Person</TH>
        <TH>Type</TH>
        <TH>Dates</TH>
        <TH numeric>Days</TH>
        <TH>Reason</TH>
        <TH>Status</TH>
        <TH>
          <span className="sr-only">Decide</span>
        </TH>
      </THead>
      <TBody>
        {requests.map((request) => (
          <TR key={request.id} onClick={() => onView(request)}>
            <TDPrimary>
              <span className="flex items-center gap-2">
                <Avatar name={request.employee_name} size="sm" />
                <span className="truncate">{request.employee_name ?? "Unnamed"}</span>
              </span>
            </TDPrimary>
            <TD>{leaveLabel(request.leave_type)}</TD>
            <TD className="whitespace-nowrap font-mono text-xs">
              {formatDate(request.start_date, "d MMM")}
              {request.start_date !== request.end_date &&
                ` to ${formatDate(request.end_date, "d MMM")}`}
            </TD>
            <TD numeric>{request.days}</TD>
            <TD className="max-w-[24ch]">
              <ReasonCell reason={request.reason} onOpen={() => onView(request)} />
            </TD>
            <TD>
              <span className="flex flex-wrap items-center gap-1.5">
                <StatusBadge status={request.status} />
                <FiledLateBadge request={request} />
              </span>
            </TD>
            <TD>
              {request.status === "pending" ? (
                <span className="flex items-center gap-1.5">
                  {/* The row opens the detail dialog, so these stop the click. */}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDecide(request, "approved");
                    }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDecide(request, "rejected");
                    }}
                  >
                    Decline
                  </Button>
                </span>
              ) : (
                <span className="whitespace-nowrap text-xs text-faint">
                  {request.decided_at ? formatTimestampDate(request.decided_at, "d MMM") : "—"}
                </span>
              )}
            </TD>
          </TR>
        ))}
      </TBody>
    </TableWrap>
  );
}
